/**
 * Notificaciones transaccionales (Super Admin) — sobre Amazon SES.
 * Edita asunto + mensaje, activa/desactiva, y manda una prueba de cada aviso.
 * El motor y el catalogo viven en backend core.notificaciones.
 */
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Bell, Loader2, Save, Send, Power, Image as ImageIcon, X } from 'lucide-react';
import { toast } from 'sonner';

import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';

const ETIQUETAS = {
  usuario_alta: 'Alta de usuario',
  usuario_baja: 'Baja de usuario',
  producto_baja: 'Baja de producto',
  licencia_baja: 'Cambio de licencia',
  password_reset: 'Recuperación de contraseña',
  lead_asignado: 'Lead asignado (al que lo recibe)',
  lead_reasignado: 'Lead reasignado (al que lo pierde)',
  lead_asignado_masivo: 'Lead asignado (reasignacion masiva)',
  lead_reasignado_masivo: 'Lead reasignado (reasignacion masiva)',
  lead_nuevo: 'Lead nuevo',
  oportunidad_ganada: 'Oportunidad ganada',
  oportunidad_perdida: 'Oportunidad perdida',
};

export default function Notificaciones() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [guardando, setGuardando] = useState('');
  const [probando, setProbando] = useState('');
  const [subiendo, setSubiendo] = useState('');
  const [correoPrueba, setCorreoPrueba] = useState('');
  const [rolesDisp, setRolesDisp] = useState([]);
  const [etiquetaRol, setEtiquetaRol] = useState({});

  const cargar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/backoffice/notificaciones');
      setItems(data.notificaciones || []);
      setRolesDisp(data.roles_disponibles || []);
      setEtiquetaRol(data.etiqueta_rol || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudieron cargar las notificaciones');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { cargar(); }, []);

  const actualizar = (tipo, campo, valor) =>
    setItems((prev) => prev.map((it) => (it.tipo === tipo ? { ...it, [campo]: valor } : it)));

  const toggleRol = (it, rol) => {
    const roles = it.roles || [];
    const nuevos = roles.includes(rol) ? roles.filter((r) => r !== rol) : [...roles, rol];
    actualizar(it.tipo, 'roles', nuevos);
  };

  const guardar = async (it) => {
    setGuardando(it.tipo);
    try {
      await api.put(`/backoffice/notificaciones/${it.tipo}`, {
        activo: it.activo, asunto: it.asunto, cuerpo: it.cuerpo,
        header_url: it.header_url || '', roles: it.roles || [],
      });
      toast.success('Notificación guardada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setGuardando('');
    }
  };

  const subirHeader = async (it, file) => {
    if (!file) return;
    setSubiendo(it.tipo);
    try {
      const fd = new FormData();
      fd.append('archivo', file);
      const { data } = await api.post(`/backoffice/notificaciones/${it.tipo}/header`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      actualizar(it.tipo, 'header_url', data.header_url);
      toast.success('Imagen de cabecera subida');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo subir la imagen');
    } finally {
      setSubiendo('');
    }
  };

  const probar = async (it) => {
    const para = correoPrueba.trim();
    if (!para) { toast.error('Escribe un correo arriba para la prueba'); return; }
    setProbando(it.tipo);
    try {
      await api.post(`/backoffice/notificaciones/${it.tipo}/probar`, { para });
      toast.success(`Prueba enviada a ${para}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo enviar la prueba');
    } finally {
      setProbando('');
    }
  };

  if (user?.role !== 'superadmin') return <Navigate to="/dashboard" replace />;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="notificaciones-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
          <Bell className="w-6 h-6 text-sky-400" /> Notificaciones
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Avisos automáticos por correo (Amazon SES, desde LeadCEM). Edita el asunto y el mensaje,
          actívalos o desactívalos, y manda una prueba. Usa variables como{' '}
          <span className="font-mono">{'{{nombre}}'}</span>.
        </p>
      </div>

      <div className="flex items-end gap-2 max-w-md">
        <div className="flex-1 space-y-1.5">
          <Label className="text-xs">Correo para «Enviar prueba»</Label>
          <Input
            value={correoPrueba}
            onChange={(e) => setCorreoPrueba(e.target.value)}
            placeholder="tucorreo@ejemplo.com"
            className="bg-black/20 border-white/10"
            data-testid="notif-correo-prueba"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((it) => (
            <Card key={it.tipo} className="bg-card/50 border-white/10" data-testid={`notif-${it.tipo}`}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-white flex items-center justify-between gap-2">
                  <span>{ETIQUETAS[it.tipo] || it.tipo}</span>
                  <Button
                    size="sm"
                    variant={it.activo ? 'default' : 'outline'}
                    onClick={() => actualizar(it.tipo, 'activo', !it.activo)}
                    className={it.activo ? 'bg-emerald-600 hover:bg-emerald-700' : 'border-white/10 text-muted-foreground'}
                    data-testid={`notif-toggle-${it.tipo}`}
                  >
                    <Power className="w-3.5 h-3.5 mr-1" />{it.activo ? 'Activa' : 'Apagada'}
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Imagen de cabecera (opcional)</Label>
                  {it.header_url ? (
                    <div className="flex items-center gap-3">
                      <img src={it.header_url} alt="" className="h-12 max-w-[200px] rounded border border-white/10 object-cover" />
                      <label className="cursor-pointer text-xs text-sky-400 hover:text-sky-300">
                        {subiendo === it.tipo ? 'Subiendo…' : 'Cambiar'}
                        <input type="file" accept="image/*" className="hidden"
                          onChange={(e) => subirHeader(it, e.target.files?.[0])} />
                      </label>
                      <button type="button" onClick={() => actualizar(it.tipo, 'header_url', '')}
                        className="text-xs text-rose-400 hover:text-rose-300 inline-flex items-center gap-0.5">
                        <X className="w-3 h-3" /> Quitar
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer inline-flex w-fit items-center gap-1.5 text-xs text-sky-400 hover:text-sky-300 border border-dashed border-white/15 rounded-md px-3 py-2">
                      {subiendo === it.tipo ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImageIcon className="w-3 h-3" />}
                      Subir imagen
                      <input type="file" accept="image/*" className="hidden"
                        onChange={(e) => subirHeader(it, e.target.files?.[0])} />
                    </label>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Asunto</Label>
                  <Input
                    value={it.asunto || ''}
                    onChange={(e) => actualizar(it.tipo, 'asunto', e.target.value)}
                    className="bg-black/20 border-white/10"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Mensaje</Label>
                  <textarea
                    value={it.cuerpo || ''}
                    onChange={(e) => actualizar(it.tipo, 'cuerpo', e.target.value)}
                    rows={3}
                    className="w-full rounded-md bg-black/20 border border-white/10 p-2 text-sm text-white font-mono"
                  />
                </div>
                {(it.variables || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 items-center">
                    <span className="text-[11px] text-muted-foreground">Variables:</span>
                    {it.variables.map((v) => (
                      <Badge key={v} variant="outline" className="text-[10px] border-white/10 font-mono">
                        {`{{${v}}}`}
                      </Badge>
                    ))}
                  </div>
                )}
                {it.destino === 'roles' ? (
                  <div className="space-y-1.5">
                    <Label className="text-xs">¿A quién le llega?</Label>
                    <div className="flex flex-wrap gap-2">
                      {rolesDisp.map((rol) => {
                        const on = (it.roles || []).includes(rol);
                        return (
                          <button
                            key={rol} type="button" onClick={() => toggleRol(it, rol)}
                            className={`text-xs px-2.5 py-1 rounded-full border transition ${on
                              ? 'bg-sky-500/20 border-sky-400/40 text-sky-300'
                              : 'border-white/10 text-muted-foreground hover:border-white/20'}`}
                          >
                            {etiquetaRol[rol] || rol}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <p className="text-[11px] text-muted-foreground">
                    Le llega a la <b>persona que corresponde</b> (p. ej. el vendedor asignado, o quien pidió el cambio).
                  </p>
                )}
                <div className="flex items-center justify-end gap-2 pt-1">
                  <Button
                    size="sm" variant="outline" onClick={() => probar(it)}
                    disabled={probando === it.tipo}
                    className="border-white/10 text-xs"
                  >
                    {probando === it.tipo
                      ? <Loader2 className="w-3 h-3 animate-spin mr-1" />
                      : <Send className="w-3 h-3 mr-1" />}
                    Enviar prueba
                  </Button>
                  <Button
                    size="sm" onClick={() => guardar(it)}
                    disabled={guardando === it.tipo}
                    className="bg-sky-500 hover:bg-sky-600 text-white text-xs"
                  >
                    {guardando === it.tipo
                      ? <Loader2 className="w-3 h-3 animate-spin mr-1" />
                      : <Save className="w-3 h-3 mr-1" />}
                    Guardar
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
